import {
  calculateCapacity,
  MAX_SERVER_ALLOCATED_CCU,
  maxAffordableCcu,
  PRODUCT_TYPE_POLICIES,
  type ProductType,
} from "@woven/contracts";
import { useState } from "react";

export function useCapacityDraft(
  budget: number | null,
  initial = { productType: "app" as ProductType, tickRateHz: 10, allocatedCcu: 1 },
  { clampToBudget = true } = {}
) {
  const [productType, setProductType] = useState(initial.productType);
  const [rate, setRate] = useState(String(initial.tickRateHz));
  const [ccu, setCcu] = useState(String(initial.allocatedCcu));
  const policy = PRODUCT_TYPE_POLICIES[productType];
  const tickRateHz = Number(rate);
  const allocatedCcu = Number(ccu);
  const validRate =
    rate !== "" &&
    Number.isInteger(tickRateHz) &&
    tickRateHz >= policy.minTickRateHz &&
    tickRateHz <= policy.maxTickRateHz;
  const maximum = budget !== null && validRate ? maxAffordableCcu(budget, tickRateHz) : 0;
  const clamped =
    clampToBudget &&
    budget !== null &&
    validRate &&
    maximum > 0 &&
    Number.isInteger(allocatedCcu) &&
    allocatedCcu > maximum;
  const resolvedCcu = clamped ? maximum : allocatedCcu;
  if (clamped) setCcu(String(maximum));
  const validCcu =
    ccu !== "" &&
    Number.isInteger(resolvedCcu) &&
    resolvedCcu >= 1 &&
    resolvedCcu <= MAX_SERVER_ALLOCATED_CCU;
  const affordable = budget !== null && resolvedCcu <= maximum;
  const capacity = validRate && validCcu ? calculateCapacity(tickRateHz, resolvedCcu) : null;
  return {
    productType,
    rate,
    ccu: clamped ? String(maximum) : ccu,
    tickRateHz,
    allocatedCcu: resolvedCcu,
    policy,
    maximum,
    validRate,
    validCcu,
    capacity,
    affordable,
    valid: affordable && capacity !== null,
    setRate,
    setCcu,
    selectProduct(value: ProductType) {
      setProductType(value);
      setRate(String(PRODUCT_TYPE_POLICIES[value].defaultTickRateHz));
    },
    setConfiguration(value: typeof initial) {
      setProductType(value.productType);
      setRate(String(value.tickRateHz));
      setCcu(String(value.allocatedCcu));
    },
    reset() {
      setProductType(initial.productType);
      setRate(String(initial.tickRateHz));
      setCcu(String(initial.allocatedCcu));
    },
  };
}
